import { apiClient } from '@/config/api';
import { API_BASE_URL } from '@/config/apiBaseUrl';

export type MeasurementDomain = {
  id: number;
  code: string;
};

export type ValueType = {
  id: number;
  code: string;
};

export type Unit = {
  id: number;
  code: string;
};

export type Metric = {
  id: number;
  code: string;
  domain: MeasurementDomain;
};

export type MetricVariant = {
  id: number;
  code: string;
  metric: Metric;
};

export type AttributeTypeMapping = {
  id: number;
  attributeType: {
    id: number;
    code: string;
    name?: string;
  };
  measurementDomain: MeasurementDomain;
  metric: Metric;
  variant: MetricVariant | null;
  valueType: ValueType;
  unit: Unit | null;
};

export type AttributeType = {
  id: number;
  code: string;
  name?: string;
};

export type CheckMappingRow = {
  attributeType: string;
  domain: string;
  metric: string;
  variant: string | null;
  valueType: string;
  unit: string | null;
  currentValue: unknown;
  currentTimestamp: string | null;
  status: 'ok' | 'no_attribute' | 'no_value';
};

export type CheckMappingResponse = {
  projectId: number;
  generatedAt: string;
  boxesCount: number;
  mappingsCount: number;
  rows: CheckMappingRow[];
  message?: string;
};

type HydraCollection<T> = {
  'hydra:member': T[];
  'hydra:totalItems': number;
  member?: T[];
  totalItems?: number;
};

const getCollection = async <T>(endpoint: string, params?: Record<string, any>): Promise<T[]> => {
  // API Platform default pagination is enabled in this project; for this admin page we want full lists.
  const finalParams = { pagination: false, ...(params ?? {}) };
  const response = await apiClient.get<HydraCollection<T> | T[]>(endpoint, { params: finalParams });
  const data = response.data;
  if (Array.isArray(data)) {
    return data;
  }
  return data['hydra:member'] || data.member || [];
};

const iri = (path: string): string => {
  // API Platform accepts absolute IRI; keep it consistent with the current API base URL.
  if (!path.startsWith('/')) return `${API_BASE_URL}/${path}`;
  return `${API_BASE_URL}${path}`;
};

export const metricCatalogService = {
  // Measurement Domains
  async getDomains(search?: string): Promise<MeasurementDomain[]> {
    const params: Record<string, any> = {};
    if (search) {
      params['code'] = search;
    }
    return getCollection<MeasurementDomain>('/measurement_domains', params);
  },

  async createDomain(code: string): Promise<MeasurementDomain> {
    const { data } = await apiClient.post<MeasurementDomain>('/measurement_domains', { code });
    return data;
  },

  async updateDomain(id: number, code: string): Promise<MeasurementDomain> {
    const { data } = await apiClient.put<MeasurementDomain>(`/measurement_domains/${id}`, { code });
    return data;
  },

  async deleteDomain(id: number): Promise<void> {
    await apiClient.delete(`/measurement_domains/${id}`);
  },

  // Value Types
  async getValueTypes(search?: string): Promise<ValueType[]> {
    const params: Record<string, any> = {};
    if (search) {
      params['code'] = search;
    }
    return getCollection<ValueType>('/value_types', params);
  },

  async createValueType(code: string): Promise<ValueType> {
    const { data } = await apiClient.post<ValueType>('/value_types', { code });
    return data;
  },

  async updateValueType(id: number, code: string): Promise<ValueType> {
    const { data } = await apiClient.put<ValueType>(`/value_types/${id}`, { code });
    return data;
  },

  async deleteValueType(id: number): Promise<void> {
    await apiClient.delete(`/value_types/${id}`);
  },

  // Units
  async getUnits(search?: string): Promise<Unit[]> {
    const params: Record<string, any> = {};
    if (search) {
      params['code'] = search;
    }
    return getCollection<Unit>('/units', params);
  },

  async createUnit(code: string): Promise<Unit> {
    const { data } = await apiClient.post<Unit>('/units', { code });
    return data;
  },

  async updateUnit(id: number, code: string): Promise<Unit> {
    const { data } = await apiClient.put<Unit>(`/units/${id}`, { code });
    return data;
  },

  async deleteUnit(id: number): Promise<void> {
    await apiClient.delete(`/units/${id}`);
  },

  // Metrics
  async getMetrics(domainId?: number, search?: string): Promise<Metric[]> {
    const params: Record<string, any> = {};
    if (domainId) {
      params['domain'] = domainId;
    }
    if (search) {
      params['code'] = search;
    }
    return getCollection<Metric>('/metrics', params);
  },

  async createMetric(domainId: number, code: string): Promise<Metric> {
    const { data } = await apiClient.post<Metric>('/metrics', {
      domain: iri(`/measurement_domains/${domainId}`),
      code,
    });
    return data;
  },

  async updateMetric(id: number, domainId: number, code: string): Promise<Metric> {
    const { data } = await apiClient.put<Metric>(`/metrics/${id}`, {
      domain: iri(`/measurement_domains/${domainId}`),
      code,
    });
    return data;
  },

  async deleteMetric(id: number): Promise<void> {
    await apiClient.delete(`/metrics/${id}`);
  },

  // Metric Variants
  async getVariants(metricId?: number, search?: string): Promise<MetricVariant[]> {
    const params: Record<string, any> = {};
    if (metricId) {
      params['metric'] = metricId;
    }
    if (search) {
      params['code'] = search;
    }
    return getCollection<MetricVariant>('/metric_variants', params);
  },

  async createVariant(metricId: number, code: string): Promise<MetricVariant> {
    const { data } = await apiClient.post<MetricVariant>('/metric_variants', {
      metric: iri(`/metrics/${metricId}`),
      code,
    });
    return data;
  },

  async updateVariant(id: number, metricId: number, code: string): Promise<MetricVariant> {
    const { data } = await apiClient.put<MetricVariant>(`/metric_variants/${id}`, {
      metric: iri(`/metrics/${metricId}`),
      code,
    });
    return data;
  },

  async deleteVariant(id: number): Promise<void> {
    await apiClient.delete(`/metric_variants/${id}`);
  },

  // Attribute Types (read-only for mapping)
  async getAttributeTypes(search?: string): Promise<AttributeType[]> {
    const params: Record<string, any> = {};
    if (search) {
      params['code'] = search;
    }
    return getCollection<AttributeType>('/attribute_types', params);
  },

  // Attribute Type Mappings
  async getMappings(search?: string): Promise<AttributeTypeMapping[]> {
    const params: Record<string, any> = {};
    if (search) {
      params['attributeType.code'] = search;
    }
    return getCollection<AttributeTypeMapping>('/attribute_type_mappings', params);
  },

  async createMapping(
    attributeTypeId: number,
    measurementDomainId: number,
    metricId: number,
    variantId: number | null,
    valueTypeId: number,
    unitId: number | null
  ): Promise<AttributeTypeMapping> {
    const payload: any = {
      attributeType: iri(`/attribute_types/${attributeTypeId}`),
      measurementDomain: iri(`/measurement_domains/${measurementDomainId}`),
      metric: iri(`/metrics/${metricId}`),
      valueType: iri(`/value_types/${valueTypeId}`),
    };
    if (variantId) {
      payload.variant = iri(`/metric_variants/${variantId}`);
    }
    if (unitId) {
      payload.unit = iri(`/units/${unitId}`);
    }
    const { data } = await apiClient.post<AttributeTypeMapping>('/attribute_type_mappings', payload);
    return data;
  },

  async updateMapping(
    id: number,
    attributeTypeId: number,
    measurementDomainId: number,
    metricId: number,
    variantId: number | null,
    valueTypeId: number,
    unitId: number | null
  ): Promise<AttributeTypeMapping> {
    const payload: any = {
      // PUT is a full replace in API Platform: keep required relation.
      attributeType: iri(`/attribute_types/${attributeTypeId}`),
      measurementDomain: iri(`/measurement_domains/${measurementDomainId}`),
      metric: iri(`/metrics/${metricId}`),
      valueType: iri(`/value_types/${valueTypeId}`),
    };
    if (variantId) {
      payload.variant = iri(`/metric_variants/${variantId}`);
    } else {
      payload.variant = null;
    }
    if (unitId) {
      payload.unit = iri(`/units/${unitId}`);
    } else {
      payload.unit = null;
    }
    const { data } = await apiClient.put<AttributeTypeMapping>(`/attribute_type_mappings/${id}`, payload);
    return data;
  },

  // Check mapping vs Zipato current values (1 row per attribute type)
  async checkMapping(projectId: number): Promise<CheckMappingResponse> {
    const { data } = await apiClient.get<CheckMappingResponse>(`/admin/metric-catalog/projects/${projectId}/check-mapping`);
    return data;
  },

  async deleteMapping(id: number): Promise<void> {
    await apiClient.delete(`/attribute_type_mappings/${id}`);
  },
};
